import { FirebaseError } from "firebase/app";
import {
  connectFunctionsEmulator,
  getFunctions,
  httpsCallable,
  type Functions,
} from "firebase/functions";

import { getFirebaseClient } from "../firebase/firebaseClient";
import type { PlayerAccountType, SubmitGameResultInput } from "./firestoreSchema";

export interface PublicPlayerProfile {
  uid: string;
  accountType: PlayerAccountType;
  publicPlayerId: string;
  publicAlias: string | null;
  aliasType: "generated" | "chosen" | null;
}

export interface SubmitGameResultResponse {
  alreadySaved: boolean;
  publicAlias: string;
  bestScore: number;
  latestScore: number;
}

export interface PlayerDataService {
  ensurePlayerProfile(): Promise<PublicPlayerProfile>;
  claimPlayerAlias(alias: string): Promise<PublicPlayerProfile>;
  submitGameResult(input: SubmitGameResultInput): Promise<SubmitGameResultResponse>;
}

let singletonService: PlayerDataService | null | undefined;
let emulatorConnected = false;

function dataErrorMessage(error: unknown): string {
  if (!(error instanceof FirebaseError)) {
    return "Az adatmentés most nem sikerült. Kérjük, próbáld újra.";
  }

  switch (error.code) {
    case "functions/already-exists":
      return error.message || "Ez a becenév vagy játékazonosító már használatban van.";
    case "functions/failed-precondition":
    case "functions/invalid-argument":
    case "functions/resource-exhausted":
      return error.message || "Az adatokat nem sikerült elfogadni.";
    case "functions/unauthenticated":
      return "A mentéshez előbb válassz vendégjátékot vagy jelentkezz be.";
    case "functions/unavailable":
      return "A mentési szolgáltatás most nem érhető el. Próbáld újra később.";
    default:
      return "Az adatmentés most nem sikerült. Kérjük, próbáld újra.";
  }
}

class FirebasePlayerDataService implements PlayerDataService {
  constructor(private readonly functions: Functions) {}

  async ensurePlayerProfile(): Promise<PublicPlayerProfile> {
    try {
      const call = httpsCallable<Record<string, never>, PublicPlayerProfile>(
        this.functions,
        "ensurePlayerProfile",
      );
      return (await call({})).data;
    } catch (error) {
      throw new Error(dataErrorMessage(error));
    }
  }

  async claimPlayerAlias(alias: string): Promise<PublicPlayerProfile> {
    try {
      const call = httpsCallable<{ alias: string }, PublicPlayerProfile>(
        this.functions,
        "claimPlayerAlias",
      );
      return (await call({ alias })).data;
    } catch (error) {
      throw new Error(dataErrorMessage(error));
    }
  }

  async submitGameResult(input: SubmitGameResultInput): Promise<SubmitGameResultResponse> {
    try {
      const call = httpsCallable<SubmitGameResultInput, SubmitGameResultResponse>(
        this.functions,
        "submitGameResult",
      );
      return (await call(input)).data;
    } catch (error) {
      throw new Error(dataErrorMessage(error));
    }
  }
}

function createPlayerDataService(): PlayerDataService | null {
  const client = getFirebaseClient();
  if (!client) {
    return null;
  }

  const functions = getFunctions(client.app, "europe-west1");
  if (client.settings.useFunctionsEmulator && !emulatorConnected) {
    connectFunctionsEmulator(
      functions,
      client.settings.functionsEmulatorHost,
      client.settings.functionsEmulatorPort,
    );
    emulatorConnected = true;
  }
  return new FirebasePlayerDataService(functions);
}

export function getPlayerDataService(): PlayerDataService | null {
  if (singletonService === undefined) {
    singletonService = createPlayerDataService();
  }
  return singletonService;
}
